import type { CurrencyCode } from '@bbpos/types';
import type { SubscriptionPlan } from '@/types/subscription';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { Button, Badge } from '@bbpos/ui';
import { cn, GLASS_PANEL, planStatusStyle, planTone, toneClasses } from './design-tokens';
import { METERED_LIMITS, formatLimit, planFeatureLabel } from './plan-entitlements';
import { planPrice } from './plan-pricing';

interface PlanCardProps {
  plan: SubscriptionPlan;
  billingCycle: 'monthly' | 'yearly';
  /** Price the plan for a tenant in this currency (INR fallback); defaults to the viewer's tenant currency. */
  currency?: CurrencyCode;
  current?: boolean;
  canEdit?: boolean;
  /** Platform view only: shows whether the plan is on the orio360.com pricing page. */
  showWebsiteStatus?: boolean;
  onSelect?: (plan: SubscriptionPlan) => void;
  onEdit?: (plan: SubscriptionPlan) => void;
  selectLabel?: string;
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function PlanCard({
  plan,
  billingCycle,
  currency: tenantCurrency,
  current,
  canEdit,
  showWebsiteStatus,
  onSelect,
  onEdit,
  selectLabel = 'Choose plan',
}: PlanCardProps) {
  const viewerCurrency = useTenantCurrency();
  const { amount: price, currency } = planPrice(plan, tenantCurrency ?? viewerCurrency, billingCycle);
  const per = billingCycle === 'yearly' ? '/ year' : '/ month';
  const tone = planTone(plan.name);
  const palette = toneClasses(tone);
  const status = planStatusStyle(plan.is_active);
  const statusPalette = toneClasses(status.tone);

  return (
    <div
      className={cn(
        GLASS_PANEL,
        'relative overflow-hidden p-6 transition-all duration-200 hover:shadow-lg animate-slide-up',
        status.border,
        current && 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-neutral-950',
        current && tone === 'success' && 'ring-emerald-500',
        current && tone === 'warning' && 'ring-amber-500',
        current && tone === 'brand' && 'ring-indigo-500',
        current && tone === 'info' && 'ring-sky-500',
      )}
    >
      <div className={cn('absolute inset-x-0 top-0 h-20 bg-gradient-to-b', palette.accent)} />
      {/* Status rail — green when the plan is active, red when it is not. */}
      <div className={cn('absolute inset-y-0 left-0 w-1', statusPalette.dot)} aria-hidden="true" />

      <div className="relative">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-400 dark:text-neutral-500">
              {plan.name}
            </p>
            <h3 className="mt-1 text-xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50">
              {plan.display_name}
            </h3>
          </div>
          <div className="flex flex-col items-end gap-1">
            {plan.badge && <Badge variant="warning">{plan.badge}</Badge>}
            {current && <Badge variant="success">Current plan</Badge>}
            {!current && <Badge variant={status.tone}>{status.label}</Badge>}
            {showWebsiteStatus && plan.show_on_website && <Badge variant="brand">On website</Badge>}
          </div>
        </div>

        <div className="mt-5">
          <p className="flex items-baseline gap-1">
            <span className="text-3xl font-semibold tabular-nums text-neutral-950 dark:text-neutral-50">
              {formatCurrency(price, currency)}
            </span>
            <span className="text-sm text-neutral-500 dark:text-neutral-400">{per}</span>
          </p>
          {plan.description && (
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{plan.description}</p>
          )}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 border-t border-neutral-100 dark:border-neutral-800/60 pt-4 text-xs">
          <div>
            <p className="uppercase tracking-wide text-neutral-400 dark:text-neutral-500">Locations</p>
            <p className="mt-0.5 text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {plan.max_locations}
            </p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-neutral-400 dark:text-neutral-500">Users</p>
            <p className="mt-0.5 text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {plan.max_users}
            </p>
          </div>
          {METERED_LIMITS.map((l) => (
            <div key={l.column}>
              <p className="uppercase tracking-wide text-neutral-400 dark:text-neutral-500">{l.label}</p>
              <p className="mt-0.5 text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                {formatLimit(l, plan[l.column] ?? null)}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
          {plan.features?.length ?? 0} feature{plan.features?.length === 1 ? '' : 's'}
        </p>
        {plan.features?.length > 0 && (
          <ul className="mt-2 space-y-2">
            {plan.features.map((f) => (
              <li
                key={f}
                className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-300"
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                    palette.icon,
                  )}
                >
                  <CheckIcon />
                </span>
                <span>{planFeatureLabel(f)}</span>
              </li>
            ))}
          </ul>
        )}

        {plan.active_tenant_count !== undefined && (
          <p className="mt-5 text-xs tabular-nums text-neutral-400 dark:text-neutral-500">
            {plan.active_tenant_count} active tenant{plan.active_tenant_count === 1 ? '' : 's'}
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {onSelect && (
            <Button
              variant={current ? 'secondary' : 'primary'}
              size="sm"
              onClick={() => onSelect(plan)}
              disabled={current || !plan.is_active}
            >
              {current ? 'Active' : selectLabel}
            </Button>
          )}
          {canEdit && onEdit && (
            <Button variant="ghost" size="sm" onClick={() => onEdit(plan)}>
              Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
